// Self-hosted Geist fonts (bundled, no external CDN) + the global CSS the mock
// relies on: keyframes (spin/blink/pulse/barflow), custom scrollbars, selection
// colour and the dark page background. Mounted once by the custom app shell.
import '@fontsource/geist-sans/400.css';
import '@fontsource/geist-sans/500.css';
import '@fontsource/geist-sans/600.css';
import '@fontsource/geist-sans/700.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';

import { color, font } from './tokens';

const CSS = `
:root { color-scheme: dark; }
.ntt-scope, .ntt-scope *, .ntt-scope *::before, .ntt-scope *::after { box-sizing: border-box; }
html, body { margin: 0; background: ${color.bg}; }
body {
  color: ${color.text};
  font-family: ${font.sans};
  -webkit-font-smoothing: antialiased;
}
/* The custom shell owns the full-height dark layout; neutralise Backstage's
   default light page chrome so nothing leaks through around our shell. */
#root { min-height: 100vh; background: ${color.bg}; }

.ntt-spin { animation: ntt-spin .9s linear infinite; }
@keyframes ntt-spin { to { transform: rotate(360deg); } }
.ntt-cur { animation: ntt-blink 1.05s steps(1) infinite; }
@keyframes ntt-blink { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }
.ntt-pulse { animation: ntt-pulse 1.4s ease-in-out infinite; }
@keyframes ntt-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
@keyframes ntt-barflow { from { background-position: 0 0; } to { background-position: 28px 0; } }

/* Confirm dialog: fade the scrim, pop the card. */
.ntt-modal-backdrop { animation: ntt-fade .13s ease-out; }
@keyframes ntt-fade { from { opacity: 0; } to { opacity: 1; } }
.ntt-modal-card { animation: ntt-modal-in .16s cubic-bezier(.2,.8,.3,1); }
@keyframes ntt-modal-in { from { opacity: 0; transform: translateY(6px) scale(.985); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .ntt-modal-backdrop, .ntt-modal-card { animation: none; }
}

::selection { background: rgba(123,116,240,.35); }
.ntt-scope input::placeholder, .ntt-scope textarea::placeholder { color: ${color.textPlaceholder}; }
.ntt-scope input, .ntt-scope select, .ntt-scope button, .ntt-scope textarea { font-family: inherit; }
.ntt-scope select { appearance: none; -webkit-appearance: none; }

*::-webkit-scrollbar { width: 11px; height: 11px; }
*::-webkit-scrollbar-thumb { background: #242a34; border-radius: 7px; border: 3px solid ${color.bg}; }
*::-webkit-scrollbar-thumb:hover { background: #2f3641; }
*::-webkit-scrollbar-track { background: transparent; }

/* --- Responsive overrides -------------------------------------------------
   The pages are built with inline styles, which win over class rules at their
   normal specificity — so the DESKTOP layout is untouched. These media queries
   only take effect on narrow viewports (half-screen window / phone) and use
   !important purely to override the inline grid/flex values there, collapsing
   the fixed multi-column layouts so nothing overlaps. */
@media (max-width: 980px) {
  .ntt-r-split { flex-direction: column !important; }
  .ntt-r-rail { width: 100% !important; position: static !important; }
}
@media (max-width: 900px) {
  .ntt-r-col2 { grid-template-columns: 1fr !important; }
  .ntt-r-col3 { grid-template-columns: repeat(2, 1fr) !important; }
}
@media (max-width: 620px) {
  .ntt-r-wrap { flex-wrap: wrap !important; }
  .ntt-r-col3 { grid-template-columns: 1fr !important; }
  /* Collapse the sidebar to a slim icon rail so it doesn't eat a phone screen.
     Labels/search/user-name hide; icons and nav stay reachable. */
  .ntt-shell-aside { width: 56px !important; padding-left: 7px !important; padding-right: 7px !important; }
  .ntt-shell-hide { display: none !important; }
  .ntt-shell-navitem { justify-content: center !important; padding-left: 0 !important; padding-right: 0 !important; }
  .ntt-shell-footer { flex-direction: column !important; gap: 8px !important; padding-left: 0 !important; padding-right: 0 !important; }
}
`;

export const GlobalStyles = () => <style>{CSS}</style>;
